'use strict';

var SLOT_SEND_DELAY_MS = 100;
var PLP_SEND_DELAY_MS = 50;
var pendingSlotTimers = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
var pendingSlotTimerIds = [];
var plpImpressionTimer = null;

/**
 * Clone bootstrap impression event with an empty impressions list.
 * @param {Object} eventTemplate
 * @returns {Object}
 */
function createImpressionEvent(eventTemplate) {
    var eventToFill = JSON.parse(JSON.stringify(eventTemplate));
    eventToFill.ecommerce = eventToFill.ecommerce || {};
    eventToFill.ecommerce.impressions = [];
    return eventToFill;
}

/**
 * Reads tiles inside a container and pushes one productImpression event.
 * Already-processed tiles are skipped (data-tracking-product-tile is removed).
 * @param {ParentNode} container
 * @param {Object} eventTemplate
 */
function getTilesAndSendEvent(container, eventTemplate) {
    if (!container || !container.querySelectorAll) {
        return;
    }

    var nodes = container.querySelectorAll('[data-tracking-product-tile]');
    if (!nodes.length) {
        return;
    }

    var eventToFill = createImpressionEvent(eventTemplate);
    var indexWithoutPosition = 1;

    nodes.forEach(function (node) {
        var view = JSON.parse(node.dataset.trackingProductTile);
        var tileContainer = node.closest('[data-tracking-position]');
        var tilePosition = tileContainer ? parseInt(tileContainer.dataset.trackingPosition, 10) : undefined;
        var tileObj = Object.assign({}, ...view);
        if (!tilePosition) {
            node.dataset.trackingPosition = indexWithoutPosition;
            tilePosition = indexWithoutPosition;
            indexWithoutPosition += 1;
        }
        tileObj.position = tilePosition;
        tileObj.list = window.TRACKING_PAGE_ACTION;
        eventToFill.ecommerce.impressions.push(tileObj);

        delete node.dataset.trackingProductTile;
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventToFill);
}

/**
 * Resolve the recommender/slot root so one event covers all tiles in that slot.
 * Intentionally excludes broad wrappers like .page-designer-wrapper so PLP grid
 * updates are not also handled by recommendation observers.
 * @param {HTMLElement} node
 * @returns {HTMLElement|null}
 */
function getRecommendationRoot(node) {
    if (!(node instanceof HTMLElement)) {
        return null;
    }

    return node.closest('.product-recommendation')
        || node.closest('.einstein-product-recommendations')
        || node.closest('[id^=cq_recomm_slot]')
        || null;
}

/**
 * Debounce impression send per recommender root.
 * @param {HTMLElement} node
 * @param {Object} eventTemplate
 */
function scheduleRecommendationImpression(node, eventTemplate) {
    var root = getRecommendationRoot(node);
    if (!root) {
        return;
    }

    if (pendingSlotTimers) {
        if (pendingSlotTimers.has(root)) {
            clearTimeout(pendingSlotTimers.get(root));
        }
        pendingSlotTimers.set(root, setTimeout(function () {
            pendingSlotTimers.delete(root);
            getTilesAndSendEvent(root, eventTemplate);
        }, SLOT_SEND_DELAY_MS));
        return;
    }

    var existing = pendingSlotTimerIds.find(function (entry) {
        return entry.root === root;
    });
    if (existing) {
        clearTimeout(existing.timer);
        existing.timer = setTimeout(function () {
            pendingSlotTimerIds = pendingSlotTimerIds.filter(function (entry) {
                return entry.root !== root;
            });
            getTilesAndSendEvent(root, eventTemplate);
        }, SLOT_SEND_DELAY_MS);
        return;
    }

    pendingSlotTimerIds.push({
        root: root,
        timer: setTimeout(function () {
            pendingSlotTimerIds = pendingSlotTimerIds.filter(function (entry) {
                return entry.root !== root;
            });
            getTilesAndSendEvent(root, eventTemplate);
        }, SLOT_SEND_DELAY_MS)
    });
}

/**
 * Debounced PLP grid impression send (load more / filter / sort).
 * @param {Object} eventTemplate
 */
function schedulePlpImpression(eventTemplate) {
    clearTimeout(plpImpressionTimer);
    plpImpressionTimer = setTimeout(function () {
        var grid = document.querySelector('.products-grid-wrapper') || document;
        getTilesAndSendEvent(grid, eventTemplate);
    }, PLP_SEND_DELAY_MS);
}

function getImpressionEventFromBootstrap() {
    var dataEl = document.getElementById('product-tiles-tracking-data');
    if (!dataEl) {
        return null;
    }

    var view;
    try {
        view = JSON.parse(dataEl.textContent);
    } catch (e) {
        return null;
    }

    if (!Array.isArray(view)) {
        return null;
    }

    return view.find(function (obj) {
        return obj.event === 'productImpression';
    }) || null;
}

/**
 * Keep observing recommendation slots; do not disconnect after the first hit.
 * @param {string} selector
 * @param {Object} eventTemplate
 */
function observeSlots(selector, eventTemplate) {
    var targets = document.querySelectorAll(selector);
    if (!targets.length) {
        return;
    }

    var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            mutation.addedNodes.forEach(function (addedNode) {
                if (!(addedNode instanceof HTMLElement)) {
                    return;
                }

                var hasTiles = addedNode.hasAttribute('data-tracking-product-tile')
                    || !!(addedNode.querySelector && addedNode.querySelector('[data-tracking-product-tile]'));
                var isRecommendationShell = addedNode.classList.contains('product-recommendation')
                    || addedNode.classList.contains('swiper-notification')
                    || addedNode.classList.contains('slick-list');

                if (!hasTiles && !isRecommendationShell) {
                    return;
                }

                scheduleRecommendationImpression(
                    addedNode.classList.contains('swiper-notification')
                        ? (addedNode.parentElement || addedNode)
                        : addedNode,
                    eventTemplate
                );
            });
        });
    });

    targets.forEach(function (slot) {
        observer.observe(slot, {
            childList: true,
            subtree: true
        });
    });
}

function initProductTilesTracking() {
    var impressionEvent = getImpressionEventFromBootstrap();
    if (!impressionEvent) {
        return;
    }

    // Initial PLP / search grid tiles
    getTilesAndSendEvent(document.querySelector('.products-grid-wrapper') || document, impressionEvent);

    // Load more / filter / sort — namespaced + debounced to avoid duplicate pushes
    $('body')
        .off('plp-grid-update.productTilesTracking')
        .on('plp-grid-update.productTilesTracking', function () {
            schedulePlpImpression(impressionEvent);
        });

    // Async CQ / Einstein slots — one debounced event per recommender root
    observeSlots('[id^=cq_recomm_slot]', impressionEvent);
    observeSlots('.page-designer-wrapper', impressionEvent);
    observeSlots('.einstein-product-recommendations', impressionEvent);
}

module.exports = {
    init: function () {
        $(document).ready(initProductTilesTracking);
    }
};
